import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMoneyBill } from 'react-icons/fa';
import { fetchData } from '../../../helpers/fetchData';
import CustomTable from '../../../shared/CustomTable';
import { formatDate } from '../../../utils/FormateDateTime';
import SkeletonCardLoading from '../../../shared/SkeltonCardLoading';

function ViewInvoiceModal({ invoiceNumber, fileNumber, onClose, isCash, patientId, documentId, simpleInvNo }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);

  useEffect(() => {
    if (invoiceNumber) {
      fetchInvoiceData();
    }
  }, [invoiceNumber, patientId, documentId, fileNumber]);

  const fetchInvoiceData = async () => {
    setLoading(true);
    try {
      const isNumeric = /^\d+$/.test(invoiceNumber);
      const payload = {
        invoice_number: isNumeric ? +invoiceNumber : simpleInvNo || 0,
        cash_invoice_number: isNumeric ? "" : invoiceNumber,
        file_number: fileNumber || '',
        patient_id: patientId || '',
        document_id: documentId || ''
      };

      const response = await fetchData(
        'POST',
        '/v3/invoices/getByNumber',
        payload
      );

      if (response?.data) {
        setInvoiceData(response.data);
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const itemColumns = [
    {
      header: t('Code'),
      field: 'item_code',
    },
    {
      header: t('Name'),
      field: 'item_name_en',
    },
    {
      header: t('Unit Price'),
      body: (row) => Number(row?.unit_price || 0).toFixed(2),
    },
    {
      header: t('Quantity'),
      field: 'quantity',
    },
    {
      header: t('Patient Share'),
      field: 'patient_share',
      body: (row) => Number(row?.patient_share || 0).toFixed(2),
    },
    {
      header: t('Patient Vat'),
      field: 'patient_tax_amount',
      body: (row) => Number(row?.patient_tax_amount || 0).toFixed(2),
    },
    ...((!isCash) ? [
      {
        header: t('Company Share'),
        field: 'company_share',
        body: (row) => Number(row?.company_share || 0).toFixed(2),
      },
      {
        header: t('Company Vat'),
        field: 'company_tax_amount',
         body: (row) => Number(row?.company_tax_amount || 0).toFixed(2),
      },
    ] : []),
    {
      header: t('Discount'),
      body: (row) => Number(row?.discount_value || 0).toFixed(2),
    },
  ];

  if (loading) {
    return (
      <SkeletonCardLoading />
    );
  }

  if (!invoiceData) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-gray-500">{t('No invoice data available')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="bg-gray-50 p-3 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-3">
          <div>
            <span className="text-sm font-semibold text-gray-700">{t('Invoice Date')}:</span>
            <p className="text-sm text-gray-800">{formatDate(invoiceData?.invoice_date) || '-'}</p>
          </div>
          <div>
            <span className="text-sm font-semibold text-gray-700">{t('Patient Name')}:</span>
            <p className="text-sm text-gray-800">{invoiceData?.patient?.full_name || '-'}</p>
          </div>
          <div>
            <span className="text-sm font-semibold text-gray-700">{t('MRN')}:</span>
            <p className="text-sm text-gray-800">{invoiceData?.patient?.file_no || '-'}</p>
          </div>
          <div>
            <span className="text-sm font-semibold text-gray-700">{t('Document ID')}:</span>
            <p className="text-sm text-gray-800">{invoiceData?.patient?.document_id || '-'}</p>
          </div>
          <div>
            <span className="text-sm font-semibold text-gray-700">{t('Nationality')}:</span>
            <p className="text-sm text-gray-800">{invoiceData?.patient?.nationality || '-'}</p>
          </div>
          <div>
            <span className="text-sm font-semibold text-gray-700">{t('Beneficiary Type')}:</span>
            <p className="text-sm text-gray-800">{invoiceData?.patient?.beneficiary_type || '-'}</p>
          </div>
          <div>
            <span className="text-sm font-semibold text-gray-600">{t('Practitioner')}:</span>
            <p className="text-sm text-gray-800">{invoiceData?.visit?.practitioner?.name || '-'}</p>
          </div>
          <div>
            <span className="text-sm font-semibold text-gray-600">{t('Department')}:</span>
            <p className="text-sm text-gray-800">{invoiceData?.visit?.practitioner?.department?.name_en || '-'}</p>
          </div>
          <div className='col-span-2'>
            <span className="text-sm font-semibold text-gray-700 ">{t('Payer Name')}:</span>
            <p className="text-sm text-gray-800">{invoiceData?.payer?.name || '-'}</p>
          </div>
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold mb-3 text-gray-700">{t('Invoice Items')}</h3>
        <CustomTable
          data={invoiceData?.invoice_details || []}
          columns={itemColumns}
          isLoading={loading}
        />
      </div>

      <div className='w-full flex items-center justify-end'>
        <div className="w-[300px]">
          <div className='bg-gray-100 p-2 rounded-md mt-2'>
            <div className='flex justify-between items-center my-2'>
              <h6 className='text-14-gray !font-[600]'>{t('Total Discount Amount')}</h6>
              <p className='text-14-dark'>{(invoiceData?.total_discount || 0)?.toFixed(2)}</p>
            </div>
          </div>

          {isCash ? (
            <>
              <div className='bg-gray-100 p-2 rounded-md mt-2'>
                <h4 className='text-[16px] text-gray-800 font-[600]'>{t('Patient Share')}</h4>
                <div className='flex justify-between items-center my-1'>
                  <h6 className='text-14-gray'>{t('Net Payable')} </h6>
                  <p className='text-14-dark'>{(invoiceData?.patient_share || 0)?.toFixed(2)}</p>
                </div>
                <div className='flex justify-between items-center my-1'>
                  <h6 className='text-14-gray'>{t('Vat')}</h6>
                  <p className='text-14-dark'>{(invoiceData?.patient_vat || 0)?.toFixed(2)}</p>
                </div>
                <div className='flex justify-between items-center my-1'>
                  <h6 className='text-14-gray'>{t('Net Payable With Vat')}</h6>
                  <p className='text-14-dark'>
                    {(invoiceData?.net_patient_payable_amount || 0)?.toFixed(2)}
                  </p>
                </div>
              </div>

            </>
          ) : (
            <>
              <div className='bg-gray-100 p-2 rounded-md mt-2'>
                <h4 className='text-gray-800 text-[16px] font-[600]'>{t('Payer Share')}</h4>
                <div className='flex items-center justify-between my-1'>
                  <h6 className='text-14-gray'>{t('Net Payable')}</h6>
                  <p className='text-14-dark'>{(invoiceData?.company_share || 0)?.toFixed(2)}</p>
                </div>
                <div className='flex items-center justify-between my-1'>
                  <h6 className='text-14-gray'>{t('Vat')}</h6>
                  <p className='text-14-dark'>{(invoiceData?.company_vat || 0)?.toFixed(2)}</p>
                </div>
                <div className='flex justify-between items-center my-1'>
                  <h6 className='text-14-gray'>{t('Net Payable With Vat')}</h6>
                  <p className='text-14-dark'>
                    {(invoiceData?.net_company_payable_amount || 0)?.toFixed(2)}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ViewInvoiceModal;
